import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Avatar, TextField, InputAdornment, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Switch, Alert, Chip, Tooltip } from '@mui/material';
import { Search, Add, Edit, Delete, PersonAdd, Business, Email, Phone, Badge, Work, Receipt } from '@mui/icons-material';
import { employeesAPI, shiftsAPI, companiesAPI } from '../services/api';

const Employees = () => {
  const [employees, setEmployees] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    employee_id: '',
    phone: '',
    department: '',
    designation: '',
    role: 'employee',
    shift_id: '',
    company_id: '',
    pan: '',
    bank_name: '',
    bank_account: '',
    bank_ifsc: '',
    pf_account: '',
    esi_account: '',
    payment_mode: 'bank',
    join_date: ''
  });
  const [leaveBalance, setLeaveBalance] = useState({ sick: 10, paid: 5 });
  const [isEditMode, setIsEditMode] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    if (loading) return; // Prevent multiple calls
    try {
      setLoading(true);
      const [empRes, shiftRes, compRes] = await Promise.all([
        employeesAPI.getAll({ search, limit: 100 }),
        shiftsAPI.getAllAdmin(),
        companiesAPI.getActive(),
      ]);
      if (empRes.data.success) setEmployees(empRes.data.data.employees);
      if (shiftRes.data.success) setShifts(shiftRes.data.data.shifts);
      if (compRes.data.success) setCompanies(compRes.data.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [search]);

  const handleSave = async () => {
    try {
      const shiftId = formData.shift_id === '' ? null : formData.shift_id;
      const companyId = formData.company_id === '' ? null : formData.company_id;
      const joinDate = formData.join_date === '' ? null : formData.join_date;

      const updateData = {
        ...formData,
        shift_id: shiftId,
        company_id: companyId,
        join_date: joinDate,
        sick_leave_balance: leaveBalance.sick,
        paid_leave_balance: leaveBalance.paid
      };

      // Remove password if empty
      if (!updateData.password) {
        delete updateData.password;
      }

      console.log('Sending update data:', updateData);

      if (isEditMode) {
        await employeesAPI.update(selectedEmployee.id, updateData);
        setMessage({ type: 'success', text: 'Employee updated successfully' });
      } else {
        await employeesAPI.register(updateData);
        setMessage({ type: 'success', text: 'Employee created successfully' });
      }
      setShowDialog(false);
      setFormData({ first_name: '', last_name: '', email: '', password: '', employee_id: '', phone: '', department: '', designation: '', role: 'employee', shift_id: '', company_id: '', pan: '', bank_name: '', bank_account: '', bank_ifsc: '', pf_account: '', esi_account: '', payment_mode: 'bank', join_date: '' });
    setLeaveBalance({ sick: 10, paid: 5 });
      setIsEditMode(false);
      setSelectedEmployee(null);
      loadData();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Operation failed' });
    }
  };
  const darkSelectStyles = {
    SelectProps: {
      MenuProps: {
        PaperProps: {
          sx: {
            bgcolor: 'rgb(10, 25, 41)',
            color: '#FFFFFF',
          },
        },
      },
    },
    sx: {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#D4AF37',
      },
      '& .MuiSelect-icon': {
        color: '#D4AF37',
      },
      '& .MuiSelect-select': {
        color: '#FFFFFF',
      },
    },
  };
  const handleEdit = (employee) => {
    setSelectedEmployee(employee);
    setFormData({
      first_name: employee.first_name || '',
      last_name: employee.last_name || '',
      email: employee.email || '',
      password: '',
      employee_id: employee.employee_id || '',
      phone: employee.phone || '',
      department: employee.department || '',
      designation: employee.designation || '',
      role: employee.role || 'employee',
      shift_id: employee.shift_id || '',
      company_id: employee.company_id || '',
      pan: employee.pan || '',
      bank_name: employee.bank_name || '',
      bank_account: employee.bank_account || '',
      bank_ifsc: employee.bank_ifsc || '',
      pf_account: employee.pf_account || '',
      esi_account: employee.esi_account || '',
      payment_mode: employee.payment_mode || 'bank',
      join_date: employee.join_date || ''
    });
    if (employee.leave_balance) {
      setLeaveBalance({
        sick: employee.leave_balance.sick || 10,
        paid: employee.leave_balance.paid || 5
      });
    }
    setIsEditMode(true);
    setShowDialog(true);
  };

  const handleDelete = () => {
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      await employeesAPI.delete(selectedEmployee.id);
      setMessage({ type: 'success', text: 'Employee deleted successfully' });
      setShowDeleteDialog(false);
      setSelectedEmployee(null);
      loadData();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Delete failed' });
    }
  };

  const handleToggleActive = async (employee) => {
    try {
      await employeesAPI.update(employee.id, { is_active: !employee.is_active });
      setMessage({ type: 'success', text: `Employee ${employee.is_active ? 'deactivated' : 'activated'} successfully` });
      loadData();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Operation failed' });
    }
  };

  const openNewDialog = () => {
    setFormData({ first_name: '', last_name: '', email: '', password: '', employee_id: '', phone: '', department: '', designation: '', role: 'employee', shift_id: '', company_id: '' });
    setLeaveBalance({ casual: 12, sick: 10, paid: 5 });
    setIsEditMode(false);
    setSelectedEmployee(null);
    setShowDialog(true);
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin':
        return { bgcolor: 'rgba(244, 67, 54, 0.2)', color: '#EF9A9A', border: '1px solid #EF9A9A' };
      case 'hr':
        return { bgcolor: 'rgba(255, 152, 0, 0.2)', color: '#FFB74D', border: '1px solid #FFB74D' };
      default:
        return { bgcolor: 'rgba(33, 150, 243, 0.2)', color: '#90CAF9', border: '1px solid #90CAF9' };
    }
  };

  return (
    <Box sx={{ bgcolor: '#0A1929', minHeight: '100vh', p: 3 }}>
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#D4AF37' }}>
            Employee Management
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<PersonAdd />} 
            onClick={openNewDialog}
            sx={{ bgcolor: '#D4AF37', color: 'rgb(10, 25, 41)', '&:hover': { bgcolor: '#C19B2E' } }}
          >
            Add Employee
          </Button>
        </Box>
        
        {message.text && (
          <Alert 
            severity={message.type} 
            onClose={() => setMessage({ type: '', text: '' })} 
            sx={{ 
              mb: 3,
              bgcolor: 'rgb(10, 25, 41)',
              color: message.type === 'success' ? '#81C784' : '#EF9A9A',
              border: `1px solid ${message.type === 'success' ? '#81C784' : '#EF9A9A'}`,
              '& .MuiAlert-icon': { color: message.type === 'success' ? '#81C784' : '#EF9A9A' }
            }}
          >
            {message.text}
          </Alert>
        )}
        
        {/* Search Bar */}
          <TextField
            fullWidth
            placeholder="Search employees by name, ID, email, or department..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: '#D4AF37' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 3,

              '& .MuiOutlinedInput-root': {
                bgcolor: 'rgb(10, 25, 41)',
                color: '#FFFFFF',
                borderRadius: 2,

                '& fieldset': {
                  borderColor: '#D4AF37',
                },

                '&:hover fieldset': {
                  borderColor: '#C19B2E',
                },

                '&.Mui-focused fieldset': {
                  borderColor: '#FFD54F', // brighter on focus
                },
              },

              // placeholder color fix
              '& input::placeholder': {
                color: '#B4C4A8',
                opacity: 1,
              },

              // icon alignment fix
              '& .MuiInputAdornment-root': {
                mr: 1,
              },
            }}
          />
        
          <Card
            sx={{
              bgcolor: 'rgb(10, 25, 41)',
              border: '1px solid rgba(212,175,55,0.3)',
              borderRadius: 3,
              overflow: 'hidden',
              boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
            }}
          >
            <CardContent sx={{ p: 0 }}>
              <TableContainer
                sx={{
                  maxHeight: 600,
                  width: '100%',
                  bgcolor: 'transparent',
                }}
              >
                <Table stickyHeader size="small">

                  {/* HEADER */}
                  <TableHead>
                    <TableRow
                      sx={{
                        '& th': {
                          bgcolor: '#22351B',
                          color: '#D4AF37',
                          fontWeight: 600,
                          borderBottom: '1px solid rgba(212,175,55,0.2)',
                        },
                      }}
                    >
                      <TableCell sx={{ minWidth: 180 }}>Employee</TableCell>
                      <TableCell sx={{ minWidth: 100 }}>ID</TableCell>
                      <TableCell sx={{ minWidth: 180 }}>Email</TableCell>
                      <TableCell sx={{ minWidth: 120 }}>Department</TableCell>
                      <TableCell sx={{ minWidth: 120 }}>Shift</TableCell>
                      <TableCell align="center" sx={{ width: 60 }}>Sick</TableCell>
                      <TableCell align="center" sx={{ width: 60 }}>Paid</TableCell>
                      <TableCell sx={{ minWidth: 80 }}>Role</TableCell>
                      <TableCell align="center" sx={{ minWidth: 80 }}>Status</TableCell>
                      <TableCell align="center" sx={{ minWidth: 100 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>

                  {/* BODY */}
                  <TableBody>
                    {employees.map((emp) => (
                      <TableRow
                        key={emp.id}
                        hover
                        sx={{
                          '& td': {
                            borderBottom: '1px solid rgba(255,255,255,0.05)',
                          },
                          '&:hover': {
                            bgcolor: 'rgba(212,175,55,0.06)',
                          },
                        }}
                      >
                        {/* Employee */}
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar
                              sx={{
                                width: 40,
                                height: 40,
                                bgcolor: '#D4AF37',
                                color: 'rgb(10, 25, 41)',
                                fontWeight: 600,
                              }}
                            >
                              {emp.first_name?.[0]}{emp.last_name?.[0]}
                            </Avatar>
                            <Box>
                              <Typography sx={{ fontWeight: 500, color: '#FFFFFF' }}>
                                {emp.first_name} {emp.last_name}
                              </Typography>
                              {emp.designation && (
                                <Typography variant="caption" sx={{ color: '#B4C4A8' }}>
                                  {emp.designation}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>

                        {/* Other cells */}
                        <TableCell sx={{ color: '#B4C4A8' }}>{emp.employee_id}</TableCell>
                        <TableCell sx={{ color: '#B4C4A8' }}>{emp.email}</TableCell>
                        <TableCell sx={{ color: '#B4C4A8' }}>{emp.department || '-'}</TableCell>

                        <TableCell sx={{ color: '#B4C4A8' }}>
                          {emp.shift
                            ? `${emp.shift.name} (${emp.shift.start_time} - ${emp.shift.end_time})`
                            : '-'}
                        </TableCell>

                        <TableCell align="center">
                          <Typography sx={{ fontWeight: 600, color: '#FFB74D' }}>
                            {emp.leave_balance?.sick || 0}
                          </Typography>
                        </TableCell>

                        <TableCell align="center">
                          <Typography sx={{ fontWeight: 600, color: '#81C784' }}>
                            {emp.leave_balance?.paid || 0}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Chip size="small" label={emp.role?.toUpperCase()} sx={getRoleColor(emp.role)} />
                        </TableCell>

                        <TableCell align="center">
                          <Switch
                            size="small"
                            checked={emp.is_active}
                            onChange={() => handleToggleActive(emp)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': { color: '#81C784' },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: '#81C784',
                              },
                              '& .MuiSwitch-track': { backgroundColor: '#EF9A9A' },
                            }}
                          />
                        </TableCell>

                        <TableCell align="center">
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(emp)}
                              sx={{ color: '#D4AF37', '&:hover': { bgcolor: 'rgba(212,175,55,0.1)' } }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Salary">
                            <IconButton
                              size="small"
                              onClick={() => window.location.href = `/payslip?employee=${emp.id}`}
                              sx={{ color: '#81C784', '&:hover': { bgcolor: 'rgba(129,199,132,0.1)' } }}
                            >
                              <Receipt fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => { setSelectedEmployee(emp); handleDelete(); }}
                              sx={{ color: '#EF9A9A', '&:hover': { bgcolor: 'rgba(239,83,80,0.1)' } }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}

                    {/* EMPTY STATE */}
                    {employees.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={10} sx={{ textAlign: 'center', py: 6 }}>
                          <Typography sx={{ color: '#B4C4A8' }}>
                            {search ? 'No employees match your search' : 'No employees found'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>

                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        
        {/* Add/Edit Employee Dialog */}
        <Dialog 
          open={showDialog} 
          onClose={() => setShowDialog(false)} 
          maxWidth="md" 
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: 'rgb(10, 25, 41)',
              border: '2px solid #D4AF37',
              borderRadius: 3,
            }
          }}
        >
          <DialogTitle sx={{ color: '#D4AF37', fontWeight: 600, borderBottom: '1px solid rgba(212,175,55,0.3)' }}>
            {isEditMode ? 'Edit Employee' : 'Add New Employee'}
          </DialogTitle>
          <DialogContent sx={{ pb: 1, pt: 2 }}>
            <Typography variant="subtitle1" sx={{ mt: 1, mb: 1, color: '#D4AF37', fontWeight: 600 }}>
              <Business sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
              Basic Information
            </Typography>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: '1fr 1fr'
              },
              gap: 2,
              mt: 1
            }}>
              <TextField 
                fullWidth 
                label="Employee ID" 
                value={formData.employee_id} 
                onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                InputLabelProps={{ sx: { color: '#B4C4A8' } }}
                InputProps={{ sx: { color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } } }}
              />
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={formData.password}
                onChange={(e) =>
                  setFormData({ ...formData, password: e.target.value })
                }
                InputLabelProps={{
                  sx: { color: '#B4C4A8' },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#FFFFFF',

                    '& fieldset': {
                      borderColor: '#D4AF37',
                    },
                    '&:hover fieldset': {
                      borderColor: '#D4AF37',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#D4AF37',
                    },
                  },
                }}
              />
              <TextField 
                fullWidth 
                label="First Name" 
                value={formData.first_name} 
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                InputLabelProps={{ sx: { color: '#B4C4A8' } }}
                InputProps={{ sx: { color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } } }}
              />
              <TextField 
                fullWidth 
                label="Last Name" 
                value={formData.last_name} 
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                InputLabelProps={{ sx: { color: '#B4C4A8' } }}
                InputProps={{ sx: { color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } } }}
              />
              <TextField 
                fullWidth 
                label="Email" 
                type="email" 
                value={formData.email} 
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                InputLabelProps={{ sx: { color: '#B4C4A8' } }}
                InputProps={{ sx: { color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } } }}
              />
              <TextField 
                fullWidth 
                label="Phone" 
                value={formData.phone} 
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                InputLabelProps={{ sx: { color: '#B4C4A8' } }}
                InputProps={{ sx: { color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } } }}
              />
              <TextField 
                fullWidth 
                label="Department" 
                value={formData.department} 
                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                InputLabelProps={{ sx: { color: '#B4C4A8' } }}
                InputProps={{ sx: { color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } } }}
              />
              <TextField 
                fullWidth 
                label="Designation" 
                value={formData.designation} 
                onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                InputLabelProps={{ sx: { color: '#B4C4A8' } }}
                InputProps={{ sx: { color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } } }}
              />
              <TextField
                select
                fullWidth
                label="Role"
                value={formData.role}
                onChange={(e) =>
                  setFormData({ ...formData, role: e.target.value })
                }
                InputLabelProps={{ sx: { color: '#B4C4A8' } }}
                {...darkSelectStyles}
              >
                <MenuItem value="employee">Employee</MenuItem>
                <MenuItem value="hr">HR</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </TextField>

              <TextField
                select
                fullWidth
                label="Shift"
                value={formData.shift_id}
                onChange={(e) =>
                  setFormData({ ...formData, shift_id: e.target.value })
                }
                InputLabelProps={{ sx: { color: '#B4C4A8' } }}
                {...darkSelectStyles}
              >
                <MenuItem value="">No Shift</MenuItem>

                {shifts.map((shift) => (
                  <MenuItem key={shift.id} value={shift.id}>
                    {shift.shift_name || shift.name} ({shift.start_time} - {shift.end_time})
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                fullWidth
                label="Company"
                value={formData.company_id}
                onChange={(e) =>
                  setFormData({ ...formData, company_id: e.target.value })
                }
                InputLabelProps={{ sx: { color: '#B4C4A8' } }}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      sx: {
                        bgcolor: 'rgb(10, 25, 41)',
                        color: '#FFFFFF',
                      },
                    },
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#D4AF37',
                  },
                  '& .MuiSelect-icon': {
                    color: '#D4AF37',
                  },
                  '& .MuiSelect-select': {
                    color: '#FFFFFF',
                  },
                }}
              >
                <MenuItem value="">No Company</MenuItem>

                {companies.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name}
                  </MenuItem>
                ))}
              </TextField>
             </Box>

             <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, color: '#D4AF37', fontWeight: 600 }}>
               <Receipt sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
               Bank Details
             </Typography>
             <Box sx={{
               display: 'grid',
               gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
               gap: 2,
               mt: 1
             }}>
               <TextField
                 fullWidth
                 label="PAN"
                 value={formData.pan}
                 onChange={(e) => setFormData({ ...formData, pan: e.target.value })}
                 InputLabelProps={{ sx: { color: '#B4C4A8' } }}
                 InputProps={{ sx: { color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } } }}
               />
               <TextField
                 fullWidth
                 label="Bank Name"
                 value={formData.bank_name}
                 onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                 InputLabelProps={{ sx: { color: '#B4C4A8' } }}
                 InputProps={{ sx: { color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } } }}
               />
               <TextField
                 fullWidth
                 label="Bank Account Number"
                 value={formData.bank_account}
                 onChange={(e) => setFormData({ ...formData, bank_account: e.target.value })}
                 InputLabelProps={{ sx: { color: '#B4C4A8' } }}
                 InputProps={{ sx: { color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } } }}
               />
               <TextField
                 fullWidth
                 label="Bank IFSC Code"
                 value={formData.bank_ifsc}
                 onChange={(e) => setFormData({ ...formData, bank_ifsc: e.target.value })}
                 InputLabelProps={{ sx: { color: '#B4C4A8' } }}
                 InputProps={{ sx: { color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } } }}
               />
               <TextField
                 fullWidth
                 label="PF Account Number"
                 value={formData.pf_account}
                 onChange={(e) => setFormData({ ...formData, pf_account: e.target.value })}
                 InputLabelProps={{ sx: { color: '#B4C4A8' } }}
                 InputProps={{ sx: { color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } } }}
               />
               <TextField
                 fullWidth
                 label="ESI Account Number"
                 value={formData.esi_account}
                 onChange={(e) => setFormData({ ...formData, esi_account: e.target.value })}
                 InputLabelProps={{ sx: { color: '#B4C4A8' } }}
                 InputProps={{ sx: { color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } } }}
               />
                <TextField
                  select
                  fullWidth
                  label="Payment Mode"
                  value={formData.payment_mode}
                  onChange={(e) =>
                    setFormData({ ...formData, payment_mode: e.target.value })
                  }
                  InputLabelProps={{ sx: { color: '#B4C4A8' } }}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        sx: {
                          bgcolor: 'rgb(10, 25, 41)',   // dropdown background
                          color: '#FFFFFF',
                        },
                      },
                    },
                  }}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#D4AF37',
                    },
                    '& .MuiSelect-icon': {
                      color: '#D4AF37',
                    },
                    '& .MuiSelect-select': {
                      color: '#FFFFFF',
                    },
                  }}
                >
                  <MenuItem value="bank">Bank Transfer</MenuItem>
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="cheque">Cheque</MenuItem>
                </TextField>
                <TextField
                  fullWidth
                  label="Joining Date"
                  type="date"
                  value={formData.join_date}
                  onChange={(e) =>
                    setFormData({ ...formData, join_date: e.target.value })
                  }
                  InputLabelProps={{
                    shrink: true,
                    sx: { color: '#B4C4A8' },
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: '#FFFFFF',
                      '& fieldset': {
                        borderColor: '#D4AF37',
                      },
                      '&:hover fieldset': {
                        borderColor: '#D4AF37',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#D4AF37',
                      },
                    },

                    // Fix calendar icon (Chrome)
                    '& input::-webkit-calendar-picker-indicator': {
                      filter: 'invert(1)', // makes icon white
                      cursor: 'pointer',
                    },

                    // Optional: background
                    '& input': {
                      color: '#FFFFFF',
                    },
                  }}
                />
             </Box>

             <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, color: '#D4AF37', fontWeight: 600 }}>
               <Badge sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
               Leave Balance (Days)
             </Typography>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: '1fr 1fr 1fr'
              },
              gap: 2,
              mt: 1
            }}>
              <TextField
                fullWidth
                label="Sick Leave"
                type="number"
                value={leaveBalance.sick}
                onChange={(e) => setLeaveBalance({ ...leaveBalance, sick: parseInt(e.target.value) || 0 })}
                inputProps={{ min: 0 }}
                InputLabelProps={{ sx: { color: '#B4C4A8' } }}
                InputProps={{ sx: { color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } } }}
              />
              <TextField
                fullWidth
                label="Paid Leave"
                type="number"
                value={leaveBalance.paid}
                onChange={(e) => setLeaveBalance({ ...leaveBalance, paid: parseInt(e.target.value) || 0 })}
                inputProps={{ min: 0 }}
                InputLabelProps={{ sx: { color: '#B4C4A8' } }}
                InputProps={{ sx: { color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } } }}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(212,175,55,0.3)' }}>
            <Button 
              onClick={() => setShowDialog(false)} 
              sx={{ color: '#B4C4A8', '&:hover': { bgcolor: 'rgba(212,175,55,0.1)' } }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={handleSave}
              sx={{ bgcolor: '#D4AF37', color: 'rgb(10, 25, 41)', '&:hover': { bgcolor: '#C19B2E' } }}
            >
              {isEditMode ? 'Update' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Delete Confirmation Dialog */}
        <Dialog 
          open={showDeleteDialog} 
          onClose={() => setShowDeleteDialog(false)}
          PaperProps={{
            sx: {
              bgcolor: 'rgb(10, 25, 41)',
              border: '2px solid #D4AF37',
              borderRadius: 3,
            }
          }}
        >
          <DialogTitle sx={{ color: '#D4AF37', fontWeight: 600 }}>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography sx={{ color: '#B4C4A8' }}>
              Are you sure you want to delete <strong className="text-gold">{selectedEmployee?.first_name} {selectedEmployee?.last_name}</strong>? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(212,175,55,0.3)' }}>
            <Button 
              onClick={() => setShowDeleteDialog(false)} 
              sx={{ color: '#B4C4A8', '&:hover': { bgcolor: 'rgba(212,175,55,0.1)' } }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              color="error" 
              onClick={confirmDelete}
              sx={{ bgcolor: '#EF4444', '&:hover': { bgcolor: '#DC2626' } }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default Employees;